import {
  createStyles,
  Flex,
  Group,
  useMantineTheme,
  Text,
  Divider,
  getStylesRef,
  Skeleton,
  MediaQuery,
  ScrollArea,
} from '@mantine/core';
import { motion } from 'framer-motion';
import React, { useEffect } from 'react';
import {AdvancedImage, lazyload, accessibility, responsive, placeholder} from '@cloudinary/react';
import { name as transformationName} from "@cloudinary/url-gen/actions/namedTransformation";
import { TierData, TierPresets } from '../GradientPicker/GradientPicker';
import { TIER_IMAGES_CDN, TIER_IMAGES_TRANSFORM_CDN } from '../../src/Constants';
import { GridDropZone, GridItem } from '../../src/GridDnD';
import { cld, getSizeForTierName, noDragStyles } from '../../src/Utils';
import { TierItemData } from './TierGrid';
import { TierLabel } from '../Create/SetupTiersScreen';

const useStyles = createStyles((theme) => ({
  tierRow: {
    paddingLeft: theme.spacing.sm,
    transition: 'none',
    flex: 1,
    flexGrow: 1,
    border: '1px solid rgba(0,0,0,0)',

    [theme.fn.smallerThan('sm')]: {
      paddingLeft: 2,
    },
  },

  imageGallery: {
    border: '1px solid rgba(0,0,0,0)',
    transition: 'all 0.2s ease',
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
    paddingLeft: theme.spacing.sm,
    ...noDragStyles,
    '&::-webkit-scrollbar': {
      height: 10,
      width: 0,
      display: 'none',
      backgroundColor: 'rgba(0,0,0,0)',
    },
    '&::-webkit-scrollbar-thumb': {
      height: 10,
      width: 0,
      backgroundColor: 'rgba(0,0,0,0)',
    },
    '&::-webkit-scrollbar-track': {
      height: 10,
      width: 0,
      backgroundColor: 'rgba(0,0,0,0)',
    },
    '&:hover::-webkit-scrollbar': {
      height: 9,
      backgroundColor: theme.colors.dark[8],
      display: 'block',
    },
    '&:hover::-webkit-scrollbar-thumb': {
      height: 9,
      backgroundColor: theme.colors.violet[2],
      borderRadius: 3,
    },
  },

  imageGalleryMobile: {
    border: '1px solid rgba(0,0,0,0)',
    transition: 'all 0.2s ease',
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
    paddingLeft: theme.spacing.sm,
    ...noDragStyles,
    position: 'fixed',
    bottom: 0,
  },

  tierFlex: {
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
    border: `1px solid ${
      theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[5]
    }`,
    userSelect: 'none',
    flex: 1,
    flexGrow: 1,
  },

  tierRowTraverse: {
    border: `1px solid ${theme.primaryColor} !important`,
  },

  tierItem: {
    overflow: 'hidden',
    aspectRatio: '1',
    // backgroundColor: theme.colors.dark[3],
    userSelect: 'none',
    MozUserSelect: 'none',
    borderRadius: theme.radius.md,
    transition: 'all 0.2s ease',
    border: `1.5px solid ${theme.colors.dark[8]}`,
    '&:hover': {
      border: `1.5px solid ${theme.colors.violet[2]}`,
      boxShadow: `3px 3px 3px rgba(0, 0, 0, 0.2)`,
    },

    [`&:hover .${getStylesRef('cardHeader')}`]: {
      opacity: 1,
    },
    position: 'relative',

    img: {
      borderRadius: theme.radius.md,
    },
  },

  cardHeader: {
    ref: getStylesRef('cardHeader'),
    position: 'absolute',
    top: 0,
    transition: 'all 0.2s ease',
    opacity: 0,
    backgroundColor:
      theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
    backdropFilter: 'blur(2px)',
    borderRadius: theme.radius.md,
  },
}));

export const TierItem = ({
  item,
  boxWidth,
  isMobile,
}: {
  item: TierItemData;
  boxWidth: number;
  isMobile: boolean;
}) => {
  const { classes } = useStyles();

  const image1x = cld.image(item.src).setDeliveryType('fetch').namedTransformation(transformationName("tier-list-image-1x"));
  const image2x = cld.image(item.src).setDeliveryType('fetch').namedTransformation(transformationName("tier-list-image-2x"));
  const image3x = cld.image(item.src).setDeliveryType('fetch').namedTransformation(transformationName("tier-list-image-3x"));


  return (
    <GridItem key={item.id}>
      <div className={classes.tierItem}>
        <div
          className="grid-item-content"
          style={{
            userSelect: 'none',
            MozUserSelect: 'none',
          }}
        >
          <AdvancedImage
            loading="lazy"
            width={`${boxWidth}px`}
            height={`${boxWidth}px`}
            decoding="async"
            plugins={[lazyload({rootMargin: '10px 20px 10px 30px', threshold: 0.25})]}
            srcset={`${image1x.toURL()} 1x, ${image2x.toURL()} 2x, ${image3x.toURL()} 2.5x, ${image3x.toURL()} 3x`}
            cldImg={image1x}
            style={{
              userSelect: 'none',
              MozUserSelect: 'none',
              ...noDragStyles,
              pointerEvents: 'none',
              aspectRatio: '1',
            }}
            src={item.src}
            alt={`${item.name} Tier List Image. Tier List Maker`}
          />
        </div>
        {item.name && (
          <>
            {isMobile ? (
              <Flex
                justify="flex-start"
                align="center"
                w="100%"
                mih={30}
                mah="100%"
                className={classes.cardHeader}
                sx={{
                  overflow: 'hidden',
                }}
                px={2}
              >
                <Text size={10} lineClamp={5}>
                  {' '}
                  {item.name.replace(/\.[^/.]+$/, '')}{' '}
                </Text>
              </Flex>
            ) : (
              <Flex
                justify="flex-start"
                align="center"
                w="100%"
                mih={30}
                mah="100%"
                px="sm"
                className={classes.cardHeader}
                sx={{
                  overflow: 'hidden',
                }}
              >
                <Text size="xs" lineClamp={5}>
                  {' '}
                  {item.name.replace(/\.[^/.]+$/, '')}{' '}
                </Text>
              </Flex>
            )}
          </>
        )}
      </div>
    </GridItem>
  );
};

export const ImageGallery = React.memo(
  ({
    idx,
    items,
    boxesPerRow,
    boxWidth,
    rowHeight,
    isMobile,
  }: {
    idx: number;
    items: TierItemData[];
    boxesPerRow: number;
    rowHeight: number;
    boxWidth: number;
    isMobile: boolean;
  }) => {
    const { classes } = useStyles();

    return (
      <GridDropZone
        className={classes.imageGallery}
        id="imageGallery"
        key="imageGallery"
        boxesPerRow={boxesPerRow}
        rowHeight={rowHeight}
        rowIndex={idx}
        traverseClassName={classes.tierRowTraverse}
        boxWidth={boxWidth}
      >
        {items.map((item) => (
          <TierItem key={item.id} item={item} boxWidth={boxWidth} isMobile={isMobile} />
        ))}
      </GridDropZone>
    );
  }
);

const TierRow = ({
  tierData,
  idx,
  items,
  boxesPerRow,
  boxWidth,
  rowHeight,
  isMobile,
}: {
  tierData: TierData;
  idx: number;
  items: TierItemData[];
  boxesPerRow: number;
  rowHeight: number;
  boxWidth: number;
  isMobile: boolean;
}) => {
  const theme = useMantineTheme();
  const { classes } = useStyles();

  const image1x = cld.image(`${TIER_IMAGES_CDN}/${tierData.imageId}`).setDeliveryType("fetch").namedTransformation(transformationName("tier-list-image-1x"));
  const image2x = cld.image(`${TIER_IMAGES_CDN}/${tierData.imageId}`).setDeliveryType("fetch").namedTransformation(transformationName("tier-list-image-2x"));
  const image3x = cld.image(`${TIER_IMAGES_CDN}/${tierData.imageId}`).setDeliveryType("fetch").namedTransformation(transformationName("tier-list-image-3x"));
  return (
    <Flex w="100%" className={classes.tierFlex} align="center">
      {tierData.preset === TierPresets.IMAGE && tierData.imageId ? (
        <AdvancedImage
          cldImg={image1x}
          srcset={`${image1x.toURL()} 1x, ${image2x.toURL()} 2x, ${image3x.toURL()} 2.5x, ${image3x.toURL()} 3x`}
          width={boxWidth}
          height={boxWidth}
          style={{
            userSelect: 'none',
            MozUserSelect: 'none',
          }}
          alt={`${tierData.name} Tier`}
        />
      ) : (
        <TierLabel
          tier={tierData}
          squareSize={boxWidth}
          styles={{
            marginLeft: isMobile ? 4 : theme.spacing.xs,
            marginRight: isMobile ? 4 : theme.spacing.xs,
            marginTop: isMobile ? 4 : theme.spacing.xs,
            marginBottom: isMobile ? 4 : theme.spacing.xs,
          }}
        />
      )}
      <Divider size="md" orientation="vertical" mr={isMobile ? 4 : theme.spacing.xs} />
      <GridDropZone
        className={classes.tierRow}
        traverseClassName={classes.tierRowTraverse}
        id={tierData.id}
        key={tierData.id}
        boxesPerRow={boxesPerRow}
        rowHeight={rowHeight}
        rowIndex={idx}
        boxWidth={boxWidth}
      >
        {items.map((item) => (
          <TierItem key={item.id} item={item} boxWidth={boxWidth} isMobile={isMobile} />
        ))}
      </GridDropZone>
    </Flex>
  );
};

const MemoRow = React.memo(TierRow);

export default MemoRow;
